import React, { useEffect, useState } from 'react';
import { getPublicAgents } from '../services/agentService';

/**
 * Landing hero "live presence" pill — signals a CROWD of agents by implying a conversation is
 * already happening: a small stack of real specialist faces + a headcount + a typing pulse. The
 * featured group slowly cross-fades through the roster, so different specialists surface over time
 * (you sense there are many). Progressive + prerender-safe: the hero renders instantly; the pill
 * fills in once the public catalog loads, and stays hidden if it can't (no layout jump, no error).
 * Honours prefers-reduced-motion (no cycling, no pulse).
 */
interface Face { id: string; name: string; avatar: string }
const GROUP = 3;
const ROTATE_MS = 10000;
const FADE_MS = 450;
/** A lively-but-not-precise online count (the real roster is fixed, so an exact number reads as
 *  static). Re-rolled each rotation so it feels live. */
const randCount = (): number => 70 + Math.floor(Math.random() * 11); // 70–80

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const LivePresence: React.FC = () => {
  const [faces, setFaces] = useState<Face[]>([]);
  const [start, setStart] = useState(0);
  const [fading, setFading] = useState(false);
  const [count, setCount] = useState(randCount);
  const reduce = prefersReducedMotion();

  useEffect(() => {
    let alive = true;
    getPublicAgents()
      .then((list) => {
        const withAvatars = list.filter((a) => a.avatar_image).map((a) => ({ id: a.id, name: a.name, avatar: a.avatar_image as string }));
        if (alive) setFaces(withAvatars);
      })
      .catch(() => undefined);
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (reduce || faces.length <= GROUP) return;
    const t = setInterval(() => {
      setFading(true);
      window.setTimeout(() => { setStart((s) => (s + GROUP) % faces.length); setCount(randCount()); setFading(false); }, FADE_MS);
    }, ROTATE_MS);
    return () => clearInterval(t);
  }, [faces.length, reduce]);

  if (faces.length < GROUP) return null; // hero stays clean until the roster is ready

  const group = Array.from({ length: GROUP }, (_, i) => faces[(start + i) % faces.length]);

  return (
    <div className="inline-flex items-center gap-2.5 px-1 py-2 text-sm text-secondary-500 dark:text-secondary-400 whitespace-nowrap">
      <span className="flex transition-opacity duration-300" style={{ opacity: fading ? 0 : 1 }}>
        {group.map((f) => (
          <img key={f.id} src={f.avatar} alt="" referrerPolicy="no-referrer"
            className="w-6 h-6 rounded-full object-cover border-2 border-white dark:border-black -ml-2 first:ml-0" />
        ))}
      </span>
      <span className="transition-opacity duration-300" style={{ opacity: fading ? 0.4 : 1 }}>
        <span className="text-secondary-700 dark:text-secondary-200 font-medium">{group[0].name}, {group[1].name}</span>
        {' '}&amp; {count} specialists online
      </span>
      <span className="flex items-center gap-0.5" aria-hidden>
        {[0, 1, 2].map((i) => (
          <span key={i} className={`w-1 h-1 rounded-full bg-secondary-400 ${reduce ? '' : 'animate-pulse'}`}
            style={reduce ? undefined : { animationDelay: `${i * 200}ms` }} />
        ))}
      </span>
    </div>
  );
};

export default LivePresence;
